<template>
  <v-layout column>
    <v-layout align-center wrap>
      <div class="title">Custom Statuses</div>

      <v-spacer />
      <v-btn
        class="primary text-none font-weight-bold"
        @click="openCreateDialog"
        >Add status</v-btn
      >
    </v-layout>

    <v-dialog
      v-model="dialogOpened"
      :fullscreen="$vuetify.breakpoint.xsOnly"
      width="800"
      persistent
      @keydown.esc="dialogOpened = false"
    >
      <v-card block flat class="px-2">
        <v-card-title class="pt-4 pb-3">
          <span class="title" v-if="selectedStatus">Edit status</span>
          <span class="title" v-else>Add new status</span>
          <v-spacer />
          <span class="caption error--text">*Required</span>
        </v-card-title>
        <v-card-text>
          <v-form ref="form1" v-model="statusValid">
            <v-layout column>
              <v-flex xs12 class="pr-2">
                <div class="text-xs-left subheading mb-1">
                  Firm-internal status:
                  <span class="error--text">*</span>
                </div>
                <v-text-field
                  name="firmUserDisplayStatus"
                  type="text"
                  v-model="firmUserDisplayStatus"
                  placeholder="e.g. In Review"
                  required
                  solo
                  class="default-input"
                />
              </v-flex>
              <v-flex xs12 class="pr-2">
                <div class="text-xs-left subheading mb-1">
                  Client-facing status:
                  <span class="error--text">*</span>
                </div>
                <v-text-field
                  name="clientDisplayStatus"
                  type="text"
                  v-model="clientDisplayStatus"
                  placeholder="e.g. In Review"
                  required
                  solo
                  class="default-input"
                />
              </v-flex>
              <v-flex xs12 class="pr-2 text-xs-left">
                <div class="mb-1 subheading">
                  Client-facing instruction:
                  <span class="error--text">*</span>
                </div>
                <quill-editor
                  v-model="clientInstruction"
                  :options="editorOption"
                  class="instruction-input default-input"
                />
              </v-flex>
            </v-layout>
          </v-form>
        </v-card-text>

        <v-card-actions>
          <v-spacer />
          <v-btn
            class="primary text-none"
            @click="submitForm()"
            :loading="submitting"
          >
            Save
          </v-btn>
          <v-btn
            class="error--text text-none"
            flat
            @click="dialogOpened = false"
          >
            {{ $t('cancel') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-data-table :headers="headers" :items="customStatuses" :loading="loading">
      <template v-slot:items="props">
        <td class="text-xs-left">
          <b>{{ props.item.firm_user_display_status }}</b>
        </td>
        <td class="text-xs-left">
          <b>{{ props.item.client_display_status }}</b>
        </td>
        <td
          class="text-xs-left text-highlight-box"
          v-html="props.item.client_instruction"
        />
        <td>
          <v-layout align-center class="ml-2">
            <v-menu
              transition="slide-y-transition"
              bottom
              nudge-bottom="30"
              class="my-1"
            >
              <v-icon slot="activator" color="primary"> more_vert </v-icon>
              <v-list>
                <v-list-tile style="cursor: pointer">
                  <v-list-tile-title @click="editStatus(props.item)">
                    Edit
                  </v-list-tile-title>
                </v-list-tile>
                <v-list-tile style="cursor: pointer">
                  <v-list-tile-title @click="deleteStatus(props.item.id)">
                    Remove
                  </v-list-tile-title>
                </v-list-tile>
              </v-list>
            </v-menu>
          </v-layout>
        </td>
      </template>
    </v-data-table>
  </v-layout>
</template>

<script>
import API from '@/api';
import has from 'lodash/has';
import get from 'lodash/get';

export default {
  name: 'CustomStatusesTab',

  props: {
    firm: {
      type: Object,
      default: null,
    },
  },

  data() {
    return {
      loading: false,
      customStatuses: [],
      headers: [
        {
          text: 'Firm-internal Status',
          align: 'left',
          value: 'firm_user_display_status',
        },
        {
          text: 'Client-facing Status',
          align: 'left',
          value: 'client_display_status',
        },
        {
          text: 'Client-facing Instruction',
          value: 'client_instruction',
        },
        {
          text: 'Actions',
          value: '',
          sortable: false,
          width: '100px',
          class: 'text-xs-center',
        },
      ],

      // For dialog
      dialogOpened: false,
      selectedStatus: null,
      clientDisplayStatus: null,
      firmUserDisplayStatus: null,
      clientInstruction: null,
      submitting: false,
      editorOption: {
        formats: ['bold', 'italic', 'underline', 'link', 'indent', 'list'],
        modules: {
          toolbar:
            window.innerWidth >= 768
              ? [
                  ['bold', 'italic', 'underline'],
                  [{ indent: '-1' }, { indent: '+1' }],
                  ['link'],
                  [{ list: 'ordered' }, { list: 'bullet' }],
                ]
              : [['bold', 'italic', 'underline']],
        },
      },
    };
  },

  methods: {
    async addStatus() {
      this.loading = true;
      await this.$v.$touch();

      if (!this.$v.$anyError) {
        const clientDisplayStatus = this.clientDisplayStatus;
        const clientInstruction = this.clientInstruction;

        this.clientInstruction = null;
        this.clientDisplayStatus = null;

        this.$v.$reset();

        await API.firm.addCustomStatus({
          firmId: this.firm.id,
          clientDisplayStatus,
          clientInstruction,
        });

        await this.getCustomStatuses();
        this.loading = false;
      }
    },

    async getCustomStatuses() {
      try {
        this.loading = true;
        const response = await API.firm.getCustomStatuses(this.firm.id);
        this.customStatuses = response.data;
      } finally {
        this.loading = false;
      }
    },

    async editStatus(selected) {
      this.selectedStatus = selected;
      this.clientDisplayStatus = selected.client_display_status;
      this.firmUserDisplayStatus = selected.firm_user_display_status;
      this.clientInstruction = selected.client_instruction;
      this.dialogOpened = true;
    },

    async deleteStatus(id) {
      try {
        await API.firm.deleteCustomStatus({
          firmId: this.firm.id,
          id,
        });
        await this.getCustomStatuses();
      } catch (error) {
        this.$store.dispatch('application/setErrorNotification', {
          message: has(error, 'response.data.error')
            ? get(error, 'response.data.error')
            : 'There was a problem deleting custom status.',
        });
      }
    },

    async openCreateDialog() {
      this.selectedStatus = null;
      this.clientDisplayStatus = null;
      this.firmUserDisplayStatus = null;
      this.clientInstruction = null;
      this.dialogOpened = true;
      this.showDuplicateAlert = false;
    },

    async submitForm() {
      try {
        this.submitting = true;

        if (this.selectedStatus) {
          await API.firm.updateCustomStatus({
            firmId: this.firmId,
            id: this.selectedStatus.id,
            clientDisplayStatus: this.clientDisplayStatus,
            firmUserDisplayStatus: this.firmUserDisplayStatus,
            clientInstruction: this.clientInstruction,
          });
        } else {
          await API.firm.addCustomStatus({
            firmId: this.firmId,
            clientDisplayStatus: this.clientDisplayStatus,
            firmUserDisplayStatus: this.firmUserDisplayStatus,
            clientInstruction: this.clientInstruction,
          });
        }

        this.dialogOpened = false;
        await this.getCustomStatuses();
      } catch (error) {
        this.$store.dispatch('application/setErrorNotification', {
          message: 'There was a problem saving custom status.',
        });
      } finally {
        this.submitting = false;
      }
    },
  },

  async created() {
    await this.getCustomStatuses();
  },
};
</script>

<style lang="postcss" scoped></style>
